// M7.3 — attention-mark design-system component (ADR-007 D-S7).
//
// PENDING DESIGN SIGN-OFF. Mark has not signed this treatment.
// ADR-007 is still proposed. The violet bar and the "AGENT" chip are
// provisional (AGENT_SHELL_VISUAL_STATUS). They are not the signed
// mark, and this file does not adopt an unsigned replacement.
//
// This renders INSIDE a surface's own row component. The surface
// decides which rows are attention targets by consulting the
// useAttentionTargets() hook; it then wraps the row's leading-edge
// column with <AgentAttentionMark>. There is NO global DOM-mutation
// registry and NO absolute-positioned overlay — attention marks are
// a child of the row's React tree, period. D-S7 §Why forbids the
// alternatives on grounds of fighting React's declarative model,
// breaking on virtualization, and shell-knows-surface coupling.
//
// Visual: a 2px violet left-bar + optional small chip. The chip is
// a design primitive the surface opts into by passing `reason` —
// without it, the component is just the left-bar, so a dense list
// (e.g. a 20-row sidebar) stays legible.
//
// Flag-off / no inputs: renders null. Enforced in two layers — the
// surface's useAttentionTargets() returns an empty Set, so .has()
// is false and the surface never mounts the component; AND the
// component itself null-checks `size` so a stray render still noops.

import type { ReactNode } from 'react';
import { Box, HStack, Text, Tooltip } from '@chakra-ui/react';
import { AGENT_SHELL_VISUAL_STATUS } from './types';

type Size = 'sm' | 'md';

type Props = {
  // Human-readable reason text — shown in the chip + tooltip.
  // When undefined, the chip is suppressed and only the left-bar renders.
  reason?: string;
  size?:   Size;
  // Optional children. When provided, this component wraps them with
  // a violet left-bar; when absent, it renders as a free-standing
  // chip + bar. The two forms cover both "wrap a row" (CatalogBrowser
  // sidebar row) and "inline marker" (standalone cell) usages without
  // forking the component.
  children?: ReactNode;
};

const BAR_WIDTH   = { sm: '2px',  md: '3px'  } as const;
const CHIP_HEIGHT = { sm: '16px', md: '20px' } as const;
const CHIP_FONT   = { sm: '9px',  md: '10px' } as const;

export function AgentAttentionMark({ reason, size = 'sm', children }: Props) {
  // Null-input defence: a component instance that materialises with
  // no reason and no children has nothing to show. Prevents a stray
  // "needs attention" bar on a row the surface accidentally mounted
  // us against.
  if (!reason && !children) return null;

  const bar = (
    <Box
      aria-hidden="true"
      flexShrink={0}
      w={BAR_WIDTH[size]}
      alignSelf="stretch"
      bg="rsViolet.400"
      borderRadius="full"
      data-testid="agent-attention-bar"
    />
  );

  const chip = reason ? (
    <Tooltip label={reason} fontSize="xs" openDelay={300}>
      <Box
        as="span"
        role="note"
        aria-label={`Agent attention: ${reason}`}
        display="inline-flex"
        alignItems="center"
        h={CHIP_HEIGHT[size]}
        px={1.5}
        bg="rsViolet.50"
        color="rsViolet.700"
        borderRadius="md"
        borderWidth="1px"
        borderColor="rsViolet.200"
        fontSize={CHIP_FONT[size]}
        fontWeight="700"
        letterSpacing="0.02em"
        data-testid="agent-attention-chip"
      >
        <Text as="span" lineHeight="1">AGENT</Text>
      </Box>
    </Tooltip>
  ) : null;

  if (!children) {
    return (
      <HStack spacing={1.5} align="center" data-testid="agent-attention-mark" data-design-status={AGENT_SHELL_VISUAL_STATUS}>
        {bar}
        {chip}
      </HStack>
    );
  }

  // Wrapping form: left-bar + content, chip pinned to the top-right
  // of the container when present. Lets a surface mark a row without
  // reflowing the row's internal grid.
  return (
    <HStack
      spacing={2}
      align="stretch"
      position="relative"
      data-testid="agent-attention-mark"
      data-design-status={AGENT_SHELL_VISUAL_STATUS}
    >
      {bar}
      <Box flex={1} minW={0}>
        {children}
      </Box>
      {chip && (
        <Box position="absolute" top="2px" right="4px">{chip}</Box>
      )}
    </HStack>
  );
}
